import { CategoricalPalette } from '../../lib/types';

interface CategoricalLegendProps<T extends string> {
  palette: CategoricalPalette<T>;
  // Extra Tailwind classes for the row container — lets the caller widen
  // the gap rhythm (FO needs `gap-x-3 gap-y-1` because its long labels wrap
  // onto multiple rows, while WHP/SD are single-line and use `gap-3`).
  rowClassName?: string;
  // Keys to render at reduced opacity. Used when the map is emphasising a
  // subset of classes (e.g. FO private-timberland focus dims federal /
  // state / local / tribal / unknown). Non-dimmed keys render at full
  // opacity. When undefined, all keys render at full opacity.
  dimKeys?: ReadonlySet<T>;
}

/**
 * Row of (color swatch + label) pairs driven by a CategoricalPalette.
 * Drops into any LegendCard body; used by the WHP, SD, and FO map legends
 * which all share the same data shape (labeled class → color).
 */
export const CategoricalLegend = <T extends string>({
  palette,
  rowClassName = 'flex items-center gap-3 flex-wrap',
  dimKeys,
}: CategoricalLegendProps<T>) => (
  <div className={rowClassName}>
    {palette.order.map(key => {
      const dimmed = dimKeys?.has(key) ?? false;
      return (
        <span
          key={key}
          className={`flex items-center gap-1.5 text-[11px] font-roboto text-neutral-300 transition-opacity ${dimmed ? 'opacity-35' : ''}`}
        >
          <span
            className="inline-block w-3 h-3 rounded-sm"
            style={{ backgroundColor: palette.colors[key] }}
          />
          {palette.labels[key]}
        </span>
      );
    })}
  </div>
);
